import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MdSearch,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import employeeAttendanceHrAPI from '../../api/employeeAttendanceHrAPI';
import { useEmployeeAttendanceAuth } from '../../context/EmployeeAttendanceAuthContext';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import {
  formatDateCompact,
  getStatusMeta,
  parseOdooDateTime,
} from '../../utils/attendanceFormatters';
import {
  daysAgoIsoLocal,
  fetchWithHrToken,
  HR_SESSION_ERROR,
  todayIsoLocal,
} from '../../utils/hrApiHelpers';
import HrEmployeeSearch from './HrEmployeeSearch';
import HrAttendanceEditModal from './HrAttendanceEditModal';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';

const PAGE_SIZE = 80;

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'early', label: 'Early' },
  { value: 'normal', label: 'On time' },
  { value: 'late', label: 'Late' },
  { value: 'absent', label: 'Absent' },
];

const VALID_STATUS = new Set(['all', 'early', 'normal', 'late', 'absent']);

function formatOdooDateShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatOdooDateTime(dateTimeStr) {
  const d = parseOdooDateTime(dateTimeStr);
  if (!d) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function statusPillClass(status) {
  const key = (status || '').toLowerCase();
  if (key === 'late') return 'ea-odoo-att-status--late';
  if (key === 'absent') return 'ea-odoo-att-status--absent';
  if (key === 'early') return 'ea-odoo-att-status--early';
  if (key === 'normal') return 'ea-odoo-att-status--normal';
  return 'ea-odoo-att-status--muted';
}

/** Odoo Many2one / id → number */
function resolveEmployeeId(value) {
  if (value == null || value === false) return null;
  if (Array.isArray(value)) {
    const n = Number(value[0]);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === 'object' && value.id != null) {
    const n = Number(value.id);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function hasPortalUserFlag(emp) {
  if (!emp) return false;
  if (emp.has_portal_access === true || emp.portal_user_created === true) return true;
  if (emp.user_id && emp.user_id !== false) return true;
  const login = emp.user_login || emp.login || emp.portal_login || emp.username;
  return Boolean(login && login !== false);
}

export default function HrAttendancePage() {
  const [searchParams] = useSearchParams();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const { user: staffUser } = useEmployeeAttendanceAuth();

  const initialFrom = searchParams.get('date_from') || daysAgoIsoLocal(30);
  const initialTo = searchParams.get('date_to') || todayIsoLocal();
  const initialStatus = VALID_STATUS.has(searchParams.get('status'))
    ? searchParams.get('status')
    : 'all';

  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);
  const [employeeId, setEmployeeId] = useState(searchParams.get('employee_id') || '');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  /** Only employees with a portal login (Portal Access records) */
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [error, setError] = useState(null);
  const [editModal, setEditModal] = useState(null);
  /** Full portal-only attendance set for client-side paging/search */
  const [allPortalRecords, setAllPortalRecords] = useState([]);

  const portalEmployeeIds = useMemo(
    () => new Set(employees.map((e) => resolveEmployeeId(e.id)).filter((id) => id != null)),
    [employees]
  );

  const portalIdsKey = useMemo(
    () => [...portalEmployeeIds].sort((a, b) => a - b).join(','),
    [portalEmployeeIds]
  );

  const loadEmployees = useCallback(async () => {
    setLoadingEmployees(true);

    // 1) Prefer Odoo Portal Access list (same ~15 rows as Portal Access menu)
    const accessList = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getPortalAccessList({ limit: 500, offset: 0 }, token)
    );

    if (accessList.error === 'no_token') {
      setError(staffUser?.hr_api_error || HR_SESSION_ERROR);
      setEmployees([]);
      setLoadingEmployees(false);
      return;
    }

    if (!accessList.error) {
      const records =
        accessList.data?.records ||
        accessList.data?.portal_access ||
        accessList.data?.items ||
        [];
      const mapped = records
        .map((r, idx) => {
          const id = resolveEmployeeId(r.employee_id || r.employee?.id || r.employee);
          if (id == null) return null;
          return {
            id,
            name:
              r.employee_name ||
              r.employee?.name ||
              (Array.isArray(r.employee_id) ? r.employee_id[1] : null) ||
              r.name ||
              `Employee #${id}`,
            department: r.department || r.department_name,
            has_portal_access: true,
            user_login: r.username || r.user_login || r.login || r.portal_user?.login || '',
            _key: r.id ?? `pa-${id}-${idx}`,
          };
        })
        .filter(Boolean);
      // Dedupe by employee id
      const byId = new Map();
      mapped.forEach((e) => {
        if (!byId.has(e.id)) byId.set(e.id, e);
      });
      setEmployees([...byId.values()]);
      setLoadingEmployees(false);
      return;
    }

    // 2) Fallback: employees flagged with portal access (not /portal-employees — that is too broad)
    const all = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getEmployees(
        { active: true, portal_only: true, limit: 500, offset: 0 },
        token
      )
    );

    if (all.error === 'no_token') {
      setError(staffUser?.hr_api_error || HR_SESSION_ERROR);
      setEmployees([]);
      setLoadingEmployees(false);
      return;
    }

    if (!all.error) {
      const list = (all.data?.employees || [])
        .filter(hasPortalUserFlag)
        .map((e) => ({
          ...e,
          id: resolveEmployeeId(e.id),
          department: e.department || e.department_name,
        }))
        .filter((e) => e.id != null);
      setEmployees(list);
      setLoadingEmployees(false);
      return;
    }

    // 3) Last resort: portal-employees, but only rows that look like real portal logins
    const portal = await fetchWithHrToken(ensureHrToken, (token) =>
      employeeAttendanceHrAPI.getPortalEmployees(token)
    );
    if (portal.error) {
      setEmployees([]);
    } else {
      const list = (portal.data?.employees || [])
        .filter(hasPortalUserFlag)
        .map((e) => ({
          ...e,
          id: resolveEmployeeId(e.id),
          department: e.department || e.department_name,
        }))
        .filter((e) => e.id != null);
      setEmployees(list);
    }
    setLoadingEmployees(false);
  }, [ensureHrToken, staffUser?.hr_api_error]);

  const loadLines = useCallback(async () => {
    // Wait until portal employees are known so we never flash "all employees"
    if (loadingEmployees) return;

    try {
      setLoading(true);
      setError(null);

      if (portalEmployeeIds.size === 0) {
        setAllPortalRecords([]);
        setSelected(new Set());
        return;
      }

      const params = {
        date_from: dateFrom,
        date_to: dateTo,
        status_filter: statusFilter || 'all',
        portal_only: true,
        limit: 5000,
        offset: 0,
      };

      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
        employeeAttendanceHrAPI.getAttendanceLines(params, token)
      );
      if (fetchError === 'no_token') {
        setError(staffUser?.hr_api_error || HR_SESSION_ERROR);
        setAllPortalRecords([]);
        return;
      }
      if (fetchError) {
        setError(fetchError);
        setAllPortalRecords([]);
        return;
      }

      const raw = data.records || data.attendance_lines || [];
      setAllPortalRecords(
        raw.filter((r) => {
          const id = resolveEmployeeId(r.employee_id);
          return id != null && portalEmployeeIds.has(id);
        })
      );
      setSelected(new Set());
    } catch (err) {
      setError(err.message || 'Failed to load attendance');
      setAllPortalRecords([]);
    } finally {
      setLoading(false);
    }
    // portalIdsKey keeps the Set dependency stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ensureHrToken,
    dateFrom,
    dateTo,
    statusFilter,
    loadingEmployees,
    portalIdsKey,
    staffUser?.hr_api_error,
  ]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadLines();
  }, [loadLines]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim().toLowerCase());
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Drop stale non-portal employee filter if list reloads
  useEffect(() => {
    if (!employeeId || loadingEmployees) return;
    if (!portalEmployeeIds.has(Number(employeeId))) {
      setEmployeeId('');
      setOffset(0);
    }
  }, [employeeId, loadingEmployees, portalEmployeeIds]);

  const selectedEmployee = employees.find((e) => String(e.id) === String(employeeId));

  const filteredRecords = useMemo(() => {
    let list = allPortalRecords;
    if (employeeId) {
      const id = resolveEmployeeId(employeeId);
      list = list.filter((r) => resolveEmployeeId(r.employee_id) === id);
    }
    if (!search) return list;
    return list.filter(
      (r) =>
        r.employee_name?.toLowerCase().includes(search) ||
        r.name?.toLowerCase().includes(search) ||
        r.department?.toLowerCase().includes(search) ||
        r.date?.includes(search) ||
        r.day?.toLowerCase().includes(search)
    );
  }, [allPortalRecords, employeeId, search]);

  useEffect(() => {
    setTotal(filteredRecords.length);
    setRecords(filteredRecords.slice(offset, offset + PAGE_SIZE));
  }, [filteredRecords, offset]);

  const visibleRecords = records;

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const allVisibleSelected =
    visibleRecords.length > 0 &&
    visibleRecords.every((r) => selected.has(r.id || `${r.employee_id}-${r.date}`));

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(
      new Set(visibleRecords.map((r) => r.id || `${r.employee_id}-${r.date}`))
    );
  };

  const toggleOne = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openCreate = () => {
    if (!employeeId) {
      setError('Select an employee in the filters first, then click New.');
      return;
    }
    setEditModal({
      mode: 'create',
      employeeId: Number(employeeId),
      employeeName: selectedEmployee?.name,
      date: dateTo,
    });
  };

  const openRow = (rec) => {
    if (!rec.id) return;
    setEditModal({
      mode: 'edit',
      attendanceId: rec.id,
      employeeId: rec.employee_id,
      employeeName: rec.employee_name,
      date: rec.date,
      check_in: rec.check_in,
      check_out: rec.check_out,
    });
  };

  return (
    <div className="ea-hr-dash ea-odoo-emp ea-odoo-att">
      <div className="ea-odoo-control">
        <div className="ea-odoo-control__left">
          <button type="button" className="ea-odoo-new" onClick={openCreate}>
            New
          </button>
          <div className="ea-odoo-control__title">Employee Attendance</div>
        </div>

        <div className="ea-odoo-search">
          <MdSearch className="ea-odoo-search__icon" aria-hidden />
          <input
            type="search"
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search attendance"
          />
        </div>

        <div className="ea-odoo-control__right">
          <div className="ea-odoo-pager" aria-label="Pagination">
            <span>{loading ? '…' : `${rangeStart}-${rangeEnd} / ${total}`}</span>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canPrev || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label="Previous page"
            >
              <MdChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canNext || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label="Next page"
            >
              <MdChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="ea-odoo-filters">
        <label>
          <span className="ea-field-label">From</span>
          <input
            type="date"
            className="ea-odoo-filters__select"
            value={dateFrom}
            onChange={(e) => {
              setOffset(0);
              setDateFrom(e.target.value);
            }}
          />
        </label>
        <label>
          <span className="ea-field-label">To</span>
          <input
            type="date"
            className="ea-odoo-filters__select"
            value={dateTo}
            onChange={(e) => {
              setOffset(0);
              setDateTo(e.target.value);
            }}
          />
        </label>
        <label>
          <span className="ea-field-label">Status</span>
          <select
            className="ea-odoo-filters__select"
            value={statusFilter}
            onChange={(e) => {
              setOffset(0);
              setStatusFilter(e.target.value);
            }}
          >
            {STATUS_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="ea-hr-filters__employee" style={{ minWidth: 220 }}>
          <span className="ea-field-label">Portal employee</span>
          <HrEmployeeSearch
            employees={employees}
            value={employeeId}
            onChange={(id) => {
              setOffset(0);
              setEmployeeId(id);
            }}
            loading={loadingEmployees}
          />
        </label>
      </div>

      {!loadingEmployees && portalEmployeeIds.size > 0 && (
        <p className="ea-odoo-muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
          Showing attendance for {portalEmployeeIds.size} portal user
          {portalEmployeeIds.size === 1 ? '' : 's'} only.
        </p>
      )}

      {error && (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error}
        </div>
      )}

      <div className="ea-odoo-list-wrap">
        <table className="ea-odoo-list ea-odoo-att-list">
          <thead>
            <tr>
              <th className="ea-odoo-att-list__check">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              <th>Reference</th>
              <th>Employee Name</th>
              <th>Date</th>
              <th>Day</th>
              <th>Week</th>
              <th>Check In</th>
              <th>Status</th>
              <th>Check Out</th>
              <th className="ea-odoo-att-list__num">Hours Worked</th>
            </tr>
          </thead>
          <tbody>
            {loading && visibleRecords.length === 0 ? (
              <tr>
                <td colSpan={10} className="ea-hr-table__empty">
                  Loading attendance…
                </td>
              </tr>
            ) : visibleRecords.length === 0 ? (
              <tr>
                <td colSpan={10} className="ea-hr-table__empty">
                  {portalEmployeeIds.size === 0 && !loadingEmployees
                    ? 'No portal users found. Create portal access first.'
                    : 'No attendance records for portal users in this range.'}
                </td>
              </tr>
            ) : (
              visibleRecords.map((rec) => {
                const meta = getStatusMeta(rec.attendance_status);
                const rowKey = rec.id || `${rec.employee_id}-${rec.date}`;
                const label = rec.attendance_status_label || meta.label;
                const hours =
                  rec.worked_hours != null && rec.worked_hours !== false
                    ? Number(rec.worked_hours).toFixed(2)
                    : '0.00';
                return (
                  <tr
                    key={rowKey}
                    className="ea-odoo-att-list__row"
                    onClick={() => openRow(rec)}
                  >
                    <td
                      className="ea-odoo-att-list__check"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(rowKey)}
                        onChange={() => toggleOne(rowKey)}
                        aria-label={`Select ${rec.employee_name}`}
                      />
                    </td>
                    <td className="ea-odoo-list__name">
                      {rec.name ||
                        `${rec.employee_name || 'Employee'} - ${formatDateCompact(rec.date)}`}
                    </td>
                    <td>{rec.employee_name || ''}</td>
                    <td>{formatOdooDateShort(rec.date)}</td>
                    <td>{rec.day || ''}</td>
                    <td>{rec.week || ''}</td>
                    <td>{formatOdooDateTime(rec.check_in)}</td>
                    <td>
                      {label && label !== '—' ? (
                        <span
                          className={`ea-odoo-att-status ${statusPillClass(
                            rec.attendance_status
                          )}`}
                        >
                          {label}
                        </span>
                      ) : (
                        ''
                      )}
                    </td>
                    <td>{formatOdooDateTime(rec.check_out)}</td>
                    <td className="ea-odoo-att-list__num">{hours}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editModal && (
        <HrAttendanceEditModal
          ensureHrToken={ensureHrToken}
          mode={editModal.mode}
          attendanceId={editModal.attendanceId}
          employeeId={editModal.employeeId}
          employeeName={editModal.employeeName}
          date={editModal.date}
          initialCheckIn={editModal.check_in}
          initialCheckOut={editModal.check_out}
          onClose={() => setEditModal(null)}
          onSaved={() => {
            setEditModal(null);
            loadLines();
          }}
        />
      )}
    </div>
  );
}
